import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { PulseDot } from '../components/PulseDot';
import { PublicNav } from '../components/PublicNav';
import { PublicFooter } from '../components/PublicFooter';
import { RegulatoryBanner } from '../components/RegulatoryNotice';
import { api, ApiError, NetworkError } from '../lib/api';
import type { ReactNode } from 'react';
import { PRIMARY, SIGNAL, BG, BG_DARKER, BG_CARD, BORDER, TEXT, TEXT_DIM, TEXT_FAINT } from '../theme/publicTokens';


// ============================================================================
// FONTS + GLOBAL STYLES
// ============================================================================

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700&family=Instrument+Sans:wght@400;500;600&family=JetBrains+Mono:wght@300;400;500;600&display=swap');

      * { box-sizing: border-box; }

      body {
        background: ${BG};
        color: ${TEXT};
        font-family: 'Instrument Sans', sans-serif;
        margin: 0;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      .font-display {
        font-family: 'Fraunces', serif;
        font-feature-settings: 'ss01', 'ss02';
        letter-spacing: -0.02em;
      }

      .font-mono {
        font-family: 'JetBrains Mono', monospace;
      }

      .font-body {
        font-family: 'Instrument Sans', sans-serif;
      }

      .ecg-grid {
        background-image:
          linear-gradient(${BORDER}33 1px, transparent 1px),
          linear-gradient(90deg, ${BORDER}33 1px, transparent 1px),
          linear-gradient(${BORDER}1A 1px, transparent 1px),
          linear-gradient(90deg, ${BORDER}1A 1px, transparent 1px);
        background-size: 100px 100px, 100px 100px, 20px 20px, 20px 20px;
        background-position: -1px -1px;
      }

      @keyframes ecg-draw {
        0% { stroke-dashoffset: 2000; }
        100% { stroke-dashoffset: 0; }
      }

      @keyframes pulse-dot {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.85); }
      }

      @keyframes fade-up {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes signal-glow {
        0%, 100% { filter: drop-shadow(0 0 8px ${PRIMARY}66); }
        50% { filter: drop-shadow(0 0 16px ${PRIMARY}AA); }
      }

      .anim-fade-up { animation: fade-up 0.8s ease-out forwards; }
      .anim-pulse-dot { animation: pulse-dot 1.4s ease-in-out infinite; }

      .ecg-trace {
        stroke-dasharray: 2000;
        animation: ecg-draw 6s linear infinite, signal-glow 2s ease-in-out infinite;
      }

      button { font-family: inherit; cursor: pointer; }
      input, label { font-family: inherit; }

      input:focus { outline: none; }

      .hover-lift {
        transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
      }
      .hover-lift:hover { transform: translateY(-2px); }

      ::selection { background: ${PRIMARY}; color: ${BG}; }
    `}</style>
  );
}

// ============================================================================
// ECG TRACE — animated SVG signal used as brand element
// ============================================================================

function ECGTrace({ height = 200, opacity = 0.6 }: { height?: number; opacity?: number }) {
  // Build a realistic-ish PQRST cycle, repeated across the width
  // Each cycle: baseline, P, PR, QRS, ST, T, baseline
  const cycleWidth = 240;
  const cycles = 6;
  const totalWidth = cycleWidth * cycles;
  const baseY = height / 2;

  let path = `M 0 ${baseY}`;
  for (let i = 0; i < cycles; i++) {
    const x0 = i * cycleWidth;
    // baseline
    path += ` L ${x0 + 30} ${baseY}`;
    // P wave (small bump up)
    path += ` Q ${x0 + 45} ${baseY - 10}, ${x0 + 60} ${baseY}`;
    // PR segment
    path += ` L ${x0 + 80} ${baseY}`;
    // Q (small dip down)
    path += ` L ${x0 + 88} ${baseY + 8}`;
    // R (big spike up)
    path += ` L ${x0 + 95} ${baseY - 70}`;
    // S (sharp dip down)
    path += ` L ${x0 + 102} ${baseY + 15}`;
    // back to baseline
    path += ` L ${x0 + 115} ${baseY}`;
    // ST segment
    path += ` L ${x0 + 145} ${baseY}`;
    // T wave (medium bump up)
    path += ` Q ${x0 + 175} ${baseY - 22}, ${x0 + 205} ${baseY}`;
    // baseline rest of cycle
    path += ` L ${x0 + cycleWidth} ${baseY}`;
  }

  return (
    <svg
      viewBox={`0 0 ${totalWidth} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      style={{ opacity }}
    >
      <path
        d={path}
        fill="none"
        stroke={PRIMARY}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="ecg-trace"
      />
    </svg>
  );
}

// ============================================================================
// SHARED UI BITS
// ============================================================================

function SectionLabel({ number, children }: { number: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-6 font-mono text-xs uppercase tracking-widest" style={{ color: TEXT_FAINT }}>
      <span style={{ color: PRIMARY }}>{number}</span>
      <span style={{ width: 32, height: 1, background: BORDER }} />
      <span>{children}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = {
    available: { label: 'Disponible', color: SIGNAL, bg: '#00FFA814' },
    validating: { label: 'En validación', color: '#FFB546', bg: '#FFB54614' },
    developing: { label: 'En desarrollo', color: PRIMARY, bg: `${PRIMARY}14` },
    roadmap: { label: 'Roadmap', color: TEXT_DIM, bg: '#8A95A814' },
  }[status] ?? { label: status, color: TEXT_DIM, bg: '#8A95A814' };
  return (
    <span
      className="font-mono text-xs uppercase tracking-wider px-2 py-1 rounded"
      style={{
        color: config.color,
        background: config.bg,
        border: `1px solid ${config.color}33`,
        whiteSpace: 'nowrap',
      }}
    >
      {config.label}
    </span>
  );
}

// ============================================================================
// SECTIONS
// ============================================================================

function Hero() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden ecg-grid"
      style={{ background: `radial-gradient(ellipse at 50% 0%, ${BG} 0%, ${BG_DARKER} 80%)` }}
    >
      {/* ECG trace as background */}
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none" style={{ opacity: 0.35 }}>
        <ECGTrace height={400} opacity={0.5} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 w-full">
        <div className="anim-fade-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
          <div className="flex items-center gap-3 mb-8 font-mono text-xs uppercase tracking-widest" style={{ color: TEXT_FAINT }}>
            <span style={{ color: PRIMARY }}>v1.0</span>
            <span style={{ width: 32, height: 1, background: BORDER }} />
            <span>Cuantificación longitudinal del ECG wearable</span>
          </div>
        </div>

        <h1
          className="font-display anim-fade-up"
          style={{
            fontSize: 'clamp(3rem, 9vw, 7rem)',
            lineHeight: 0.95,
            fontWeight: 400,
            color: TEXT,
            maxWidth: '14ch',
            animationDelay: '0.2s',
            opacity: 0,
          }}
        >
          De PDF a señal{' '}
          <span style={{ fontStyle: 'italic', fontWeight: 300, color: PRIMARY }}>
            cuantificable
          </span>
          .
        </h1>

        <p
          className="mt-10 font-body anim-fade-up"
          style={{
            fontSize: 'clamp(1.05rem, 1.4vw, 1.3rem)',
            lineHeight: 1.6,
            color: TEXT_DIM,
            maxWidth: '52ch',
            fontWeight: 400,
            animationDelay: '0.4s',
            opacity: 0,
          }}
        >
          ECG.WATCH cuantifica la evolución temporal de parámetros electrofisiológicos
          a partir de informes del Apple Watch, con baseline individual y framing clínico
          contextualizado.
          <br />
          <span style={{ color: TEXT_FAINT }}>
            Para cardiólogos que necesitan datos auditables. Para pacientes que quieren llevar algo más que un PDF a la consulta.
          </span>
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 anim-fade-up" style={{ animationDelay: '0.6s', opacity: 0 }}>
          <a
            href="#lista-de-espera"
            className="hover-lift inline-flex items-center justify-center gap-2 px-8 py-4 font-mono text-sm uppercase tracking-wider"
            style={{
              background: PRIMARY,
              color: BG,
              fontWeight: 600,
              borderRadius: 4,
              textDecoration: 'none',
            }}
          >
            Sumarme a la lista de espera
            <span>→</span>
          </a>
          <a
            href="#como-funciona"
            className="hover-lift inline-flex items-center justify-center gap-2 px-8 py-4 font-mono text-sm uppercase tracking-wider"
            style={{
              color: TEXT,
              border: `1px solid ${BORDER}`,
              borderRadius: 4,
              textDecoration: 'none',
            }}
          >
            Ver cómo funciona
          </a>
        </div>

        <div className="mt-8 font-mono text-xs anim-fade-up" style={{ color: TEXT_FAINT, animationDelay: '0.7s', opacity: 0 }}>
          Acceso anticipado por orden de inscripción · Sin spam
        </div>

        {/* Tech footer of hero */}
        <div
          className="absolute bottom-8 left-6 lg:left-12 right-6 lg:right-12 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-xs"
          style={{ color: TEXT_FAINT, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}
        >
          <span>SAMPLING_RATE = 512 Hz</span>
          <span>RECONSTRUCTION = exact</span>
          <span>SOURCE = vector_pdf</span>
          <span style={{ color: SIGNAL }}>● signal.live</span>
        </div>
      </div>
    </section>
  );
}

function Problema() {
  return (
    <section
      id="problema"
      className="py-32 relative"
      style={{ background: BG }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel number="01">El problema</SectionLabel>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: TEXT,
              }}
            >
              El ECG del Apple Watch llega como{' '}
              <span style={{ fontStyle: 'italic', color: TEXT_DIM }}>PDF</span>.
              <br />
              No como{' '}
              <span style={{ color: PRIMARY }}>dato clínico</span>.
            </h2>
          </div>

          <div className="lg:col-span-5 space-y-6" style={{ fontSize: '1.05rem', lineHeight: 1.7, color: TEXT_DIM }}>
            <p>
              Hoy un cardiólogo recibe un archivo que <strong style={{ color: TEXT }}>no puede medir, comparar ni auditar</strong>.
              Las apps que prometen "interpretarlo" lo hacen con modelos cerrados que ni el médico ni el paciente pueden revisar.
            </p>
            <p>
              ECG.WATCH abre esa caja: reconstruimos la señal real desde el PDF y la convertimos
              en datos cuantitativos que se pueden <strong style={{ color: TEXT }}>ver, medir y verificar</strong>.
            </p>
          </div>
        </div>

        {/* Visual contrast strip */}
        <div className="mt-20 grid md:grid-cols-2 gap-px" style={{ background: BORDER }}>
          <div style={{ background: BG_CARD, padding: '40px' }}>
            <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: TEXT_FAINT }}>
              Antes
            </div>
            <div className="font-display text-2xl mb-3" style={{ color: TEXT_DIM, fontWeight: 400 }}>
              PDF estático
            </div>
            <ul className="font-mono text-sm space-y-2" style={{ color: TEXT_FAINT }}>
              <li>— No medible</li>
              <li>— No comparable</li>
              <li>— No auditable</li>
              <li>— No reproducible</li>
            </ul>
          </div>
          <div style={{ background: BG_CARD, padding: '40px', position: 'relative' }}>
            <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: PRIMARY }}>
              Con ECG.WATCH
            </div>
            <div className="font-display text-2xl mb-3" style={{ color: TEXT, fontWeight: 400 }}>
              Señal a 512 Hz
            </div>
            <ul className="font-mono text-sm space-y-2" style={{ color: TEXT_DIM }}>
              <li style={{ color: SIGNAL }}>+ Métricas clínicas</li>
              <li style={{ color: SIGNAL }}>+ Comparación entre estudios</li>
              <li style={{ color: SIGNAL }}>+ Cada número auditable</li>
              <li style={{ color: SIGNAL }}>+ Versión y método trazables</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoFunciona() {
  const steps = [
    {
      n: '01',
      title: 'Subís el PDF',
      body: 'El informe exportado del Apple Watch (o cualquier ECG en PDF compatible).',
    },
    {
      n: '02',
      title: 'Reconstruimos la señal',
      body: 'No rasterizamos la imagen: extraemos el trazado vectorial del PDF y lo convertimos en una serie temporal a 512 Hz.',
    },
    {
      n: '03',
      title: 'Cuantificamos',
      body: 'Aplicamos algoritmos versionados con bias y precisión documentados sobre la señal reconstruida.',
    },
    {
      n: '04',
      title: 'Te mostramos todo',
      body: 'Cada métrica viene con su método, su versión, su error estándar y la porción de señal que la generó.',
    },
  ];

  return (
    <section id="como-funciona" className="py-32" style={{ background: BG_DARKER }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel number="02">Cómo funciona</SectionLabel>

        <h2
          className="font-display mb-20"
          style={{
            fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
            lineHeight: 1.05,
            fontWeight: 400,
            color: TEXT,
          }}
        >
          Cuatro pasos.
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: BORDER }}>
          {steps.map((step) => (
            <div key={step.n} className="hover-lift" style={{ background: BG_DARKER, padding: '40px 32px' }}>
              <div
                className="font-display mb-6"
                style={{ fontSize: '4rem', fontWeight: 300, color: PRIMARY, lineHeight: 1 }}
              >
                {step.n}
              </div>
              <h3 className="font-display text-2xl mb-4" style={{ color: TEXT, fontWeight: 500 }}>
                {step.title}
              </h3>
              <p style={{ color: TEXT_DIM, lineHeight: 1.6, fontSize: '0.95rem' }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QueMedimos() {
  const sections = [
    {
      title: 'Disponible hoy',
      status: 'available',
      items: [
        'Reconstrucción de señal a 512 Hz desde PDF original',
        'Frecuencia cardíaca media — hr_mean@v1.0',
        'Variabilidad de RR (SDNN) — sdnn@v1.0',
        'Intervalo PR — pr@v1.0',
        'Duración QRS — qrs@v1.0',
        'Intervalo QT y QTc Bazett/Fridericia — qt@v1.0',
        'Cada métrica con SE, n_beats, método, versión y referencia bibliográfica',
      ],
    },
    {
      title: 'En validación',
      status: 'validating',
      items: [
        'Validación primaria contra ECG-12 derivaciones (Phase A frente 2)',
        'Reproducibilidad inter-registro single-subject (n≥30 ECGs)',
        'Detección de extrasístoles',
      ],
    },
    {
      title: 'En desarrollo',
      status: 'developing',
      items: [
        'Índice de calidad de señal por ECG (alta / media / baja)',
        'Baseline personal por métrica y alertas longitudinales',
        'Análisis beat-by-beat',
      ],
    },
    {
      title: 'En el roadmap',
      status: 'roadmap',
      items: [
        'Vistas clínicas por especialidad (cardiología, psiquiatría, anestesia)',
        'Alertas farma-aware (CredibleMeds + ATC)',
        'Prescripción inteligente de ECGs (frecuencia + cronograma)',
        'App del paciente con reporte de síntomas con timestamp',
      ],
    },
  ];

  return (
    <section id="metricas" className="py-32" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel number="03">Qué medimos</SectionLabel>

        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: TEXT,
              }}
            >
              Métricas clínicas, con su alcance documentado.
            </h2>
          </div>
          <div className="lg:col-span-5" style={{ color: TEXT_DIM, fontSize: '1.05rem', lineHeight: 1.7 }}>
            <p>
              Mostramos qué está disponible hoy, qué está en validación contra ground truth,
              y qué viene en el roadmap.
              {' '}
              <strong style={{ color: TEXT }}>Nada se publica sin que esté listo.</strong>
            </p>
          </div>
        </div>

        {/* Datasheet-style table */}
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 4, overflow: 'hidden' }}>
          {sections.map((section, idx) => (
            <div
              key={section.title}
              className="grid md:grid-cols-12 gap-6"
              style={{
                padding: '32px',
                background: idx % 2 === 0 ? BG_CARD : BG,
                borderTop: idx === 0 ? 'none' : `1px solid ${BORDER}`,
              }}
            >
              <div className="md:col-span-4 flex flex-col gap-3">
                <StatusBadge status={section.status} />
                <h3 className="font-display text-2xl" style={{ color: TEXT, fontWeight: 500 }}>
                  {section.title}
                </h3>
              </div>
              <ul className="md:col-span-8 space-y-3">
                {section.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 font-mono text-sm"
                    style={{ color: TEXT_DIM, lineHeight: 1.6 }}
                  >
                    <span style={{ color: PRIMARY, marginTop: 2 }}>›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoPreview() {
  const capabilities = [
    { num: '01', label: 'Valores validados', sub: 'HR_mean, SDNN y próx. PR/QRS/QT/QTc — cada métrica versionada' },
    { num: '02', label: 'Interpretación estructurada', sub: 'Notas firmadas por el cardiólogo, audit log de cambios' },
    { num: '03', label: 'Visión longitudinal', sub: 'Timeline + baseline propio del paciente, comparación entre fechas' },
    { num: '04', label: 'Trazabilidad metodológica', sub: '/methodology pública · R-peak detection F1 99.60% vs MIT-BIH' },
  ];

  return (
    <section id="comparativa" className="py-32" style={{ background: BG_DARKER }}>
      <div className="max-w-6xl mx-auto px-8">
        <SectionLabel number="04">COMPARATIVA</SectionLabel>
        <h2
          className="font-display text-5xl md:text-6xl font-light leading-tight mb-6 max-w-4xl"
          style={{ letterSpacing: '-0.025em' }}
        >
          Apple te muestra dos palabras.<br />
          <em style={{ fontStyle: 'italic', color: PRIMARY, fontWeight: 500 }}>
            ECG.WATCH te entrega el cuadro completo.
          </em>
        </h2>
        <p
          className="text-lg max-w-2xl mb-16 leading-relaxed"
          style={{ color: TEXT_DIM }}
        >
          Comparativa visual lado a lado entre lo que recibís de Apple Watch y lo que
          ECG.WATCH te ofrece para tu práctica clínica.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {capabilities.map((cap) => (
            <div
              key={cap.num}
              className="p-6 rounded-lg"
              style={{ background: BG_CARD, border: `1px solid ${BORDER}` }}
            >
              <div
                className="font-mono text-xs mb-3"
                style={{ color: PRIMARY, letterSpacing: '0.15em' }}
              >
                {cap.num}
              </div>
              <div
                className="font-display text-xl mb-2"
                style={{ letterSpacing: '-0.01em', fontWeight: 400 }}
              >
                {cap.label}
              </div>
              <div
                className="text-sm"
                style={{ color: TEXT_DIM, lineHeight: 1.55 }}
              >
                {cap.sub}
              </div>
            </div>
          ))}
        </div>

        <Link
          to="/comparativa"
          className="inline-flex items-center gap-3 px-8 py-4 rounded font-medium"
          style={{
            background: PRIMARY,
            color: BG,
            textDecoration: 'none',
            fontSize: 15,
          }}
        >
          Ver comparativa completa
          <span style={{ fontSize: 18 }}>→</span>
        </Link>
      </div>
    </section>
  );
}

function ParaPacientes() {
  return (
    <section className="py-20" style={{ background: BG_DARKER }}>
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <SectionLabel number="07">Para pacientes</SectionLabel>

        <h2
          className="font-display mb-6"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            lineHeight: 1.15,
            fontWeight: 400,
            color: TEXT,
          }}
        >
          ¿Sos paciente y llegaste{' '}
          <span style={{ fontStyle: 'italic', color: PRIMARY }}>hasta acá</span>?
        </h2>

        <p style={{ fontSize: '1.05rem', lineHeight: 1.65, color: TEXT_DIM, maxWidth: '70ch' }}>
          ECG.WATCH no es una app de autodiagnóstico. No te decimos si tu ECG está "bien" o "mal".
          Lo que sí podemos hacer es darte un{' '}
          <strong style={{ color: TEXT }}>reporte ordenado</strong>, con la señal reconstruida y las
          métricas básicas, para que se lo lleves a tu cardiólogo.
        </p>
      </div>
    </section>
  );
}

function Vision() {
  const ejes = [
    { n: '01', title: 'Contexto del paciente', body: 'Medicación con riesgo de QT (CredibleMeds), comorbilidades estructuradas, eventos sintomáticos con timestamp.' },
    { n: '02', title: 'Histórico longitudinal', body: 'Comparar estudios del mismo paciente, detectar desviaciones de su propia línea de base, alertas farma-aware.' },
    { n: '03', title: 'Vistas clínicas por especialidad', body: 'Misma capa fisiológica, distinto framing semántico: cardiología, psiquiatría, anestesia, medicina interna, deporte. Cada vista firmada por su validador.' },
    { n: '04', title: 'Prescripción inteligente', body: 'El cardiólogo indica frecuencia y duración del seguimiento, como con cualquier estudio.' },
    { n: '05', title: 'App del paciente', body: 'Reporte de síntomas en el momento, recordatorios, historia simplificada.' },
  ];

  return (
    <section id="vision" className="py-32" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel number="05">Visión</SectionLabel>

        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: TEXT,
              }}
            >
              Más que un{' '}
              <span style={{ fontStyle: 'italic', color: TEXT_DIM }}>visor</span>
              {' '}de ECGs.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end" style={{ color: TEXT_DIM, fontSize: '1.05rem', lineHeight: 1.7 }}>
            <p>
              ECG.WATCH está construido para crecer en cinco direcciones.
              Cada paso se libera{' '}
              <strong style={{ color: TEXT }}>cuando está validado. No antes.</strong>
            </p>
          </div>
        </div>

        <div className="space-y-px" style={{ background: BORDER }}>
          {ejes.map((eje) => (
            <div
              key={eje.n}
              className="grid md:grid-cols-12 gap-6 hover-lift"
              style={{ background: BG, padding: '32px 0' }}
            >
              <div className="md:col-span-1 font-mono text-xs" style={{ color: TEXT_FAINT, letterSpacing: '0.1em', paddingTop: 6 }}>
                EJE / {eje.n}
              </div>
              <div className="md:col-span-4 font-display" style={{ fontSize: '1.5rem', color: TEXT, fontWeight: 500 }}>
                {eje.title}
              </div>
              <div className="md:col-span-7" style={{ color: TEXT_DIM, fontSize: '1rem', lineHeight: 1.6 }}>
                {eje.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ListaDeEspera() {
  const [userType, setUserType] = useState<'cardio' | 'patient'>('cardio');
  const [email, setEmail] = useState('');
  const [extra, setExtra] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.submitWaitlist({
        email: email.trim(),
        role: userType,
        extra: extra.trim() || null,
        accepted_communications: accepted,
      }),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setErrorMessage('Demasiados intentos. Esperá un minuto y volvé a probar.');
        } else if (err.status === 400 || err.status === 422) {
          setErrorMessage('Revisá el email y el consentimiento.');
        } else {
          setErrorMessage('Algo falló del lado del servidor. Probá de nuevo en un rato.');
        }
      } else if (err instanceof NetworkError) {
        setErrorMessage('No pudimos conectar. Verificá tu conexión.');
      } else {
        setErrorMessage('Error inesperado. Probá de nuevo.');
      }
    },
  });

  // submitted derivado de la mutation: preserva el render del banner gracias
  const submitted = mutation.isSuccess;
  const submitting = mutation.isPending;

  const handleSubmit = () => {
    if (!email || !accepted || submitting) return;
    setErrorMessage(null);
    mutation.mutate();
  };

  return (
    <section id="lista-de-espera" className="py-32 relative overflow-hidden" style={{ background: BG_DARKER }}>
      {/* Subtle ECG trace at top */}
      <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{ opacity: 0.2 }}>
        <ECGTrace height={120} opacity={0.4} />
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-12 relative">
        <SectionLabel number="09">Acceso anticipado</SectionLabel>

        {!submitted ? (
          <>
            <h2
              className="font-display mb-6"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: TEXT,
              }}
            >
              Sumate y te avisamos cuando sea{' '}
              <span style={{ fontStyle: 'italic', color: PRIMARY }}>tu turno</span>.
            </h2>
            <p style={{ color: TEXT_DIM, fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 48 }}>
              Estamos liberando ECG.WATCH por etapas.
            </p>

            <div className="space-y-6">
              {/* User type toggle */}
              <div>
                <label className="font-mono text-xs uppercase tracking-widest block mb-3" style={{ color: TEXT_FAINT }}>
                  ¿Quién sos?
                </label>
                <div className="grid grid-cols-2 gap-px" style={{ background: BORDER, borderRadius: 4, overflow: 'hidden' }}>
                  <button
                    onClick={() => setUserType('cardio')}
                    className="hover-lift py-4 px-4 font-mono text-sm uppercase tracking-wider transition"
                    style={{
                      background: userType === 'cardio' ? PRIMARY : BG,
                      color: userType === 'cardio' ? BG : TEXT_DIM,
                      fontWeight: 500,
                      border: 'none',
                    }}
                  >
                    Cardiólogo / Profesional
                  </button>
                  <button
                    onClick={() => setUserType('patient')}
                    className="hover-lift py-4 px-4 font-mono text-sm uppercase tracking-wider transition"
                    style={{
                      background: userType === 'patient' ? PRIMARY : BG,
                      color: userType === 'patient' ? BG : TEXT_DIM,
                      fontWeight: 500,
                      border: 'none',
                    }}
                  >
                    Paciente
                  </button>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="font-mono text-xs uppercase tracking-widest block mb-3" style={{ color: TEXT_FAINT }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full font-mono"
                  style={{
                    background: BG,
                    color: TEXT,
                    border: `1px solid ${BORDER}`,
                    padding: '14px 16px',
                    borderRadius: 4,
                    fontSize: '0.95rem',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = BORDER)}
                />
              </div>

              {/* Extra context */}
              <div>
                <label className="font-mono text-xs uppercase tracking-widest block mb-3" style={{ color: TEXT_FAINT }}>
                  {userType === 'cardio' ? 'Institución o especialidad (opcional)' : 'Comentario (opcional)'}
                </label>
                <input
                  type="text"
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder={userType === 'cardio' ? 'Ej: Servicio de Cardiología, Hospital X' : 'Algo que quieras contarnos'}
                  className="w-full font-mono"
                  style={{
                    background: BG,
                    color: TEXT,
                    border: `1px solid ${BORDER}`,
                    padding: '14px 16px',
                    borderRadius: 4,
                    fontSize: '0.95rem',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = BORDER)}
                />
              </div>

              {/* Consent */}
              <label className="flex items-start gap-3 cursor-pointer pt-2" style={{ userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  style={{ marginTop: 4, accentColor: PRIMARY, width: 16, height: 16 }}
                />
                <span style={{ color: TEXT_DIM, fontSize: '0.9rem', lineHeight: 1.5 }}>
                  Acepto recibir comunicaciones de ECG.WATCH sobre el lanzamiento del producto.
                </span>
              </label>

              {/* Error message */}
              {errorMessage && (
                <p
                  className="font-mono text-xs"
                  style={{ color: '#FF6B7A', lineHeight: 1.6, marginTop: 8 }}
                  role="alert"
                >
                  {errorMessage}
                </p>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!email || !accepted || submitting}
                className="hover-lift w-full py-5 px-8 font-mono uppercase tracking-wider mt-4"
                style={{
                  background: email && accepted && !submitting ? PRIMARY : BORDER,
                  color: email && accepted && !submitting ? BG : TEXT_FAINT,
                  border: 'none',
                  borderRadius: 4,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: email && accepted && !submitting ? 'pointer' : 'not-allowed',
                }}
              >
                {submitting ? 'Enviando...' : 'Sumarme →'}
              </button>

              <p className="font-mono text-xs" style={{ color: TEXT_FAINT, lineHeight: 1.6, marginTop: 16 }}>
                Sumate. Vamos abriendo acceso por etapas según el caso.
              </p>
            </div>
          </>
        ) : (
          <div
            style={{
              background: BG,
              border: `1px solid ${PRIMARY}`,
              padding: '60px 40px',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
              <PulseDot color={SIGNAL} size={12} />
            </div>
            <h2 className="font-display mb-6" style={{ fontSize: '2.5rem', color: TEXT, fontWeight: 400 }}>
              Estás en la lista.
            </h2>
            <p style={{ color: TEXT_DIM, fontSize: '1.05rem', lineHeight: 1.6, maxWidth: '50ch', margin: '0 auto' }}>
              Te vamos a escribir a <strong style={{ color: TEXT }}>{email}</strong> cuando abramos el siguiente cohort.
            </p>
            <div className="font-mono text-xs mt-12" style={{ color: TEXT_FAINT }}>
              registered.as = {userType === 'cardio' ? 'clinician' : 'patient'}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// MAIN
// ============================================================================

function FocoInicial() {
  const grupos = [
    { especialidad: 'Cardiología', drogas: 'Sotalol · Amiodarona · Dofetilida · Flecainida' },
    { especialidad: 'Psiquiatría', drogas: 'Haloperidol · Ziprasidona · Quetiapina · Citalopram dosis altas' },
    { especialidad: 'Infectología', drogas: 'Ciprofloxacina · Fluconazol · Ondansetrón' },
    { especialidad: 'Oncología', drogas: 'Drogas con riesgo QT documentado en CredibleMeds' },
  ];

  return (
    <section className="py-32 relative" style={{ background: BG_DARKER }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel number="06">Foco inicial</SectionLabel>

        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: TEXT,
              }}
            >
              Pacientes con drogas{' '}
              <span style={{ fontStyle: 'italic', color: PRIMARY }}>QT-prolongadoras</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end" style={{ color: TEXT_DIM, fontSize: '1.05rem', lineHeight: 1.7 }}>
            <p>
              Donde la cuantificación longitudinal del QTc tiene{' '}
              <strong style={{ color: TEXT }}>valor clínico documentado</strong>.
              Pregunta concreta, métrica acotada, workflow existente.
            </p>
          </div>
        </div>

        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 4, overflow: 'hidden' }}>
          {grupos.map((g, idx) => (
            <div
              key={g.especialidad}
              className="grid md:grid-cols-12 gap-6"
              style={{
                padding: '28px 32px',
                background: idx % 2 === 0 ? BG_CARD : BG,
                borderTop: idx === 0 ? 'none' : `1px solid ${BORDER}`,
              }}
            >
              <div className="md:col-span-3 font-display" style={{ fontSize: '1.25rem', color: TEXT, fontWeight: 500 }}>
                {g.especialidad}
              </div>
              <div className="md:col-span-9 font-mono" style={{ color: TEXT_DIM, fontSize: '0.9rem', lineHeight: 1.6 }}>
                {g.drogas}
              </div>
            </div>
          ))}
        </div>

        <p
          className="mt-12 font-body"
          style={{
            color: TEXT_FAINT,
            fontSize: '0.95rem',
            lineHeight: 1.6,
            maxWidth: '70ch',
            fontStyle: 'italic',
          }}
        >
          Otros use cases en exploración: seguimiento ambulatorio post-evento (post-ablación, post-IAM, post-CDI)
          e investigación longitudinal con wearables. La categorización regulatoria está sujeta a evaluación formal.
        </p>
      </div>
    </section>
  );
}

function CuestionarioCardiologos() {
  return (
    <section id="cardiologos" className="py-32" style={{ background: BG }}>
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <SectionLabel number="08">Para cardiólogos</SectionLabel>

        <h2
          className="font-display mb-10"
          style={{
            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            lineHeight: 1.05,
            fontWeight: 400,
            color: TEXT,
          }}
        >
          Buscamos{' '}
          <span style={{ fontStyle: 'italic', color: PRIMARY }}>puntos ciegos</span>,
          no validación.
        </h2>

        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7" style={{ fontSize: '1.05rem', lineHeight: 1.7, color: TEXT_DIM }}>
            <p style={{ marginBottom: 16 }}>
              Si sos cardiólogo y tenés ganas de tirar abajo nuestras hipótesis, te queremos escuchar.
              Si el producto sobrevive a tu crítica, se fortalece. Si no, mejor enterarnos temprano.
            </p>
            <p>
              <strong style={{ color: TEXT }}>Cuestionario clínico de 12-15 minutos</strong> — Core obligatorio +
              Deep Dive opcional. Las respuestas más valiosas son las que muestran dónde el producto NO funciona.
            </p>
          </div>

          <div className="md:col-span-5">
            <div
              style={{
                border: `1px solid ${BORDER}`,
                background: BG_CARD,
                padding: '28px',
                borderRadius: 4,
              }}
            >
              <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: PRIMARY }}>
                Las 3 preguntas críticas
              </div>
              <ul className="space-y-3" style={{ color: TEXT_DIM, fontSize: '0.95rem', lineHeight: 1.5 }}>
                <li>— ¿Qué te haría NO usar este producto?</li>
                <li>— ¿Qué afirmación te parecería inmediatamente sospechosa?</li>
                <li>— ¿Qué evidencia mínima te haría pasar de escéptico a curioso?</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <a
            href="#lista-de-espera"
            className="hover-lift inline-flex items-center gap-2 px-8 py-4 font-mono text-sm uppercase tracking-wider"
            style={{
              color: TEXT,
              border: `1px solid ${PRIMARY}`,
              borderRadius: 4,
              textDecoration: 'none',
              background: `${PRIMARY}0A`,
            }}
          >
            Cuestionario web — próximamente
            <span style={{ color: PRIMARY }}>→</span>
          </a>
          <span className="ml-4 font-mono text-xs" style={{ color: TEXT_FAINT }}>
            Mientras tanto, dejanos tu mail y te lo enviamos cuando esté listo
          </span>
        </div>
      </div>
    </section>
  );
}

export function LandingPage() {
  return (
    <div style={{ background: BG, color: TEXT, minHeight: '100vh' }}>
      <GlobalStyles />
      <PublicNav />
      <Hero />
      <Problema />
      <ComoFunciona />
      <QueMedimos />
      <DemoPreview />
      <Vision />
      <FocoInicial />
      <ParaPacientes />
      <CuestionarioCardiologos />
      <ListaDeEspera />
      <RegulatoryBanner lang="es" />
      <PublicFooter />
    </div>
  );
}
